import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { getAge } from "@/utils/dateUtil";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { ResumeCitizenAttendancesIndicatorM1Data } from "@/services/esus/indicator/emulti/m1/getResumeCitizensAttendancesIndicatorM1";
import {
	DetailedCitizenAttendancesIndicatorM1Data,
	getDetailedCitizenAttendancesIndicatorM1,
} from "@/services/esus/indicator/emulti/m1/getDetailedCitizenAttendancesIndicatorM1";

import { IndicatorEmultiM1CitizensFilters } from "./IndicatorEmultiM1CitizensFilters";
import { IndicatorEmultiM1CitizensTable } from "./IndicatorEmultiM1CitizensTable";
import { IndicatorEmultiM1CitizenAttendancesHistory } from "./IndicatorEmultiM1CitizenHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEmultiM1CitizensProps = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizens: Paginated<ResumeCitizenAttendancesIndicatorM1Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeCitizensIndicatorM1: () => Promise<ResumeCitizenAttendancesIndicatorM1Data[]>;
};

export function IndicatorEmultiM1Citizens({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizens,
	pageNumber,
	setPageNumber,
	handleGetAllResumeCitizensIndicatorM1: handleGetAllDetailedIndicatorM1,
}: Readonly<IndicatorEmultiM1CitizensProps>) {
	const { uf, ibgeCode } = useCurrentAccount();
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [detailedCitizenAttendancesIndicatorM1, setDetailedCitizenAttendancesIndicatorM1] =
		useState<DetailedCitizenAttendancesIndicatorM1Data>(
			new DetailedCitizenAttendancesIndicatorM1Data()
		);
	const [isLoadingCitizenWithAttendances, setIsLoadingCitizenWithAttendances] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(citizenId: number) {
		setIsLoadingCitizenWithAttendances(true);
		setDetailedCitizenAttendancesIndicatorM1(
			await getDetailedCitizenAttendancesIndicatorM1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				citizenId,
			})
		);
		setIsLoadingCitizenWithAttendances(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorM1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorM1AttendancesToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Cidadãos acompanhados - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorM1AttendancesToGenericObjectArray(
		citizensWithAttendances: ResumeCitizenAttendancesIndicatorM1Data[]
	) {
		return citizensWithAttendances.map(
			(citizenWithAttendances) =>
				({
					Nome: citizenWithAttendances.fullName,
					DN: citizenWithAttendances.birthdate
						? dayjs(citizenWithAttendances.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithAttendances.birthdate
						? getAge(citizenWithAttendances.birthdate)
						: "",
					Documento: citizenWithAttendances.cpf ?? citizenWithAttendances.cns ?? "",
					"Equipe Vinculada": citizenWithAttendances.healthTeamName,
					"Equipe Vinculada Ine": citizenWithAttendances.healthTeamIne,
					Area: citizenWithAttendances.area ?? "FA",
					Atendimentos: citizenWithAttendances.totalAttendances,
					"Atividades Coletivas": citizenWithAttendances.totalGroupActivities,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAttendances || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEmultiM1CitizensFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEmultiM1CitizensTable
							citizensWithAttendances={paginatedCitizens.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizens?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizens.pageSize}
								totalPages={paginatedCitizens.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atendimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEmultiM1CitizenAttendancesHistory
							detailedCitizenAttendancesIndicatorM1={
								detailedCitizenAttendancesIndicatorM1
							}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
